import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import {
  Card,
  Grid,
  Button,
  useTheme,
  CardHeader,
  Typography,
  CardContent,
  IconButton
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import {
  useGetOrganizationApplySourcesQuery,
  useUpdateApplySourcesMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';
import { RHFTextField } from 'src/components/hook-form';
import FeatureLockedButton from 'src/components/billing/feature-locked-button';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

export default function ApplicationSourcesSettings() {
  const { t } = useTranslate();
  const theme = useTheme();

  const { canAccess } = useFeatures();

  const { organization, account } = useAuthContext();

  const { currentData: applySources, isLoading: isLoadingSources } = useGetOrganizationApplySourcesQuery(organization.id);

  const [updateApplySources, { isLoading: isSubmitting }] = useUpdateApplySourcesMutation();

  const ApplicationSourcesSchema = Yup.object().shape({
    sources: Yup.array()
      .of(
        Yup.object().shape({
          source: Yup.string().required(t('validation.required')),
        })
      )
      .unique(t('validation.unique'), (value: any) => value.source)
      .min(1, t('validation.min_one_source'))
      .required(t('validation.required'))
  });

  const defaultValues = useMemo(() => ({
    sources: applySources
      ? applySources.map(source => ({ source: source.source }))
      : [{ source: '' }]
  }), [applySources]);

  const methods = useForm({
    resolver: yupResolver(ApplicationSourcesSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = methods;

  // reset form when data changes
  useMemo(() => {
    if (applySources) {
      reset({
        sources: applySources.map(source => ({ source: source.source }))
      });
    }
  }, [applySources, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sources',
  });

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  const applicationSourcesFeatureEnabled = useMemo(() =>
    true, []);

  if (isLoadingSources) {
    return (
      <Box sx={{ width: '100%' }}>
        <Card>
          <CardContent>
            <Typography>{t('common.loading')}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateApplySources({
        org_id: organization.id,
        sources: data.sources
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.application_sources.api.update.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('admin-organization.settings.application_sources.api.update.default_error'), { variant: 'error' });
    }
  });

  const handleAddSource = () => {
    append({ source: '' });
  };

  const handleRemoveSource = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="mdi:source-branch"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.application_sources.title')}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="textSecondary">
              {t('admin-organization.settings.application_sources.description')}
            </Typography>
          }
        />

        <CardContent>
          <FormProvider {...methods}>
            <Stack direction="column" justifyContent="center" sx={{ mt: 2 }} gap={2}>
              {
                fields.length === 0 && (
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {t('admin-organization.settings.application_sources.no_sources')}
                  </Typography>
                )
              }

              {fields.map((field, index) => (
                <Grid container spacing={2} key={field.id} alignItems="center">
                  <Grid item xs={11}>
                    <RHFTextField
                      name={`sources.${index}.source`}
                      label={t('admin-organization.settings.application_sources.labels.source')}
                      disabled={!canEditSettings || !applicationSourcesFeatureEnabled}
                      helperText={errors.sources?.[index]?.message}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      onClick={() => handleRemoveSource(index)}
                      disabled={!canEditSettings || !applicationSourcesFeatureEnabled || fields.length <= 1}
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              {errors.sources?.root && (
                <Typography variant="caption" color="error">
                  {errors.sources?.root.message}
                </Typography>
              )}

              {errors.sources && (
                <Typography variant="caption" color="error">
                  {errors.sources.message}
                </Typography>
              )}

              <Box>
                <Button
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  onClick={handleAddSource}
                  disabled={!canEditSettings || !applicationSourcesFeatureEnabled}
                >
                  {t('admin-organization.settings.application_sources.add_source')}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                {applicationSourcesFeatureEnabled ? (
                  <LoadingButton
                    type="submit"
                    onClick={onSubmit}
                    variant="contained"
                    loading={isSubmitting}
                    disabled={!isValid || !canEditSettings}
                  >
                    {t('common.update')}
                  </LoadingButton>
                ) : (
                  <FeatureLockedButton
                    label={t(`common.subscription_required`)}
                    variant="contained"
                  />
                )}
              </Box>
            </Stack>
          </FormProvider>
        </CardContent>
      </Card>
    </Box>
  );
}